import * as React from 'react';
import {
  StackLayout,
  ParagraphText,
  GDSHeadingLevel,
  GDSHeadingSize,
  GDSStyleObject,
} from '@leagueplatform/genesis-core';
import { Accordion } from '@leagueplatform/genesis-core-alpha';
import { getSubHeadingLevel } from '#utils/get-sub-heading-level.util.ts';
import { TierComponent } from './tier.component';
import type { AccordionData, Tier } from '../plan-usage-widget.types';
import { overrideParagraphSpacing } from '../constants/genesis-override-styles.constants';

interface AccordionPlanUsageProps {
  heading?: string;
  headingLevel?: GDSHeadingLevel;
  headingSize?: GDSHeadingSize;
  description?: string;
  id?: string;
  accordion: AccordionData;
  tiers?: Tier[];
  onAccordionClick?: () => void;
  onTooltipClick?: (tooltipId: string) => void;
  css?: GDSStyleObject;
}

export function AccordionPlanUsage({
  heading = '',
  headingLevel: headingLevelProp,
  headingSize = 'md',
  description,
  id,
  accordion,
  tiers,
  onAccordionClick: onAccordionClickCallback,
  onTooltipClick: onTooltipClickCallback,
  css,
}: AccordionPlanUsageProps) {
  const headingLevel = React.useMemo(
    () => getSubHeadingLevel(headingLevelProp, '3'),
    [headingLevelProp],
  );
  const tierHeadingLevel = React.useMemo(
    () => (heading ? getSubHeadingLevel(headingLevel) : headingLevel),
    [heading, headingLevel],
  );
  const handleAccordionClick = React.useCallback(() => {
    if (onAccordionClickCallback) {
      onAccordionClickCallback();
    }
  }, [onAccordionClickCallback]);

  return (
    <Accordion
      heading={heading}
      headingLevel={headingLevel}
      headingSize={headingSize}
      defaultExpanded={accordion.isExpanded}
      isExpanded={accordion.isExpanded}
      id={`accordion-${id}`}
      onClick={handleAccordionClick}
      dividerPosition={accordion.dividerPosition}
      css={{
        width: '100%',
        '&:hover button': { cursor: 'pointer' },
        ...css,
      }}
    >
      <StackLayout spacing="$two" css={{ width: '100%' }}>
        {description && (
          <ParagraphText
            size="sm"
            color="subdued"
            css={overrideParagraphSpacing}
          >
            {description}
          </ParagraphText>
        )}
        {tiers?.map((tier, tierIndex) => (
          <TierComponent
            key={`tier-${tier.heading?.heading || tierIndex}`}
            heading={tier.heading?.heading}
            headingSize={tier.heading?.headingSize}
            headingLevel={tierHeadingLevel}
            description={tier.description}
            accumulators={tier.accumulators}
            lastUpdated={tier.lastUpdated}
            index={tierIndex}
            onTooltipClick={onTooltipClickCallback}
          />
        ))}
      </StackLayout>
    </Accordion>
  );
}
